import { defineStore } from "pinia";
import { $axios } from "@bloglovin/vue-framework";
import { useGeneralStore } from "%/stores/general-store";
import { useConfigurationStore } from "%/stores/configuration-store";
import { useCreatorPublicationStore } from "%/stores/creator-publication-store";
import StoryError from "~/errors/story-error";
import useFanPublicationStore from "%/stores/fan-publication-store";
import { useBuilderNavigationStore } from "~/stores/builder-navigation-store";
import { useSocialNetworksStore } from "~/stores/social-networks-store";
import { useMyPublicationPostFeedStore } from "~/smart-feed/stores/feed/my-publication-post-feed-store";
import dayjs from "dayjs";
import {
  STORY_PUBLISH_STATUS_ACTIVE,
  STORY_PUBLISH_STATUS_DRAFT,
  STORY_PUBLISH_STATUS_NEEDS_EDITING,
  STORY_PUBLISH_STATUS_SCHEDULED,
  STORY_PUBLISH_STATUS_SCHEDULED_NEEDS_REVIEW,
  STORY_PUBLISH_STATUS_SUBMITTED_FOR_REVIEW,
  STORY_PUBLISH_STATUS_PUBLISH_FAILED,
} from "~/constants/story-status-constants";
import { STORY_VARIANT_TYPE_STORY } from "@/constants/story-variant-type-constants";
import { PUBLISHING_PLATFORM_TYPE } from "%/constants/story-constants";

const PUBLISH_STATUS_POLL_INTERVAL_MS = 2000;

const DEFAULT_STORY = {
  story_id: 0,
  title: "",
  status: "",
  thumbnail: "",
  assignee: [],
  assignee_id: 0,
  publishing_platform_name: "",
  publishing_platform_type: "",
  variant_parent_id: 0,
  variant_count: 0,
  segments: {},
  segment_order: [],
  timestamp_scheduled: 0,
  timestampScheduled: 0,
  tasks: [],
};

export const useStoryStore = defineStore("fanweb-story-store", {
  state: () => ({
    stories: {},
    savingStatus: "All Changes Saved",
    loadingHashtags: false,
    loadingTitle: false,
    loadingPublish: false,
    publishJobId: 0,
    publishStatusPollTimeoutId: null,
    publishError: "",
    loadingCreate: false,
    loadingUpdate: false,
    debounceTitle: null,
    debounceUpdate: null,
    updateStoryExclusivityIsLoading: false,
    updateStoryRequiresSyndicationApprovalContentIsLoading: false,
    updateStoryAllowRemixContentIsLoading: false,
    uploadThumbnailLoading: false,
    isSendingInstructions: false,
    sendingInstructionsError: "",
    isFetchingInstructions: false,
    fetchingInstructionsError: "",
    uploadStoryAudioLoading: false,
    isLoadingBuilderPageData: true,
    updateStorySeoIsLoading: false,
    activeStoryIdOverride: 0,
  }),
  getters: {
    orgId() {
      return useGeneralStore().orgId;
    },
    userId() {
      return useGeneralStore().userId;
    },
    isUserLoggedIn() {
      return useConfigurationStore().isSignupAndLoginEnabled && this.userId > 0;
    },
    currentStoryId() {
      if (this.activeStoryIdOverride > 0) {
        return this.activeStoryIdOverride;
      }
      return Number(useBuilderNavigationStore().locationContext.entityId);
    },
    currentBuilderPageData() {
      return this.stories[this.currentStoryId]?.builderPageData || null;
    },
    story() {
      return this.currentBuilderPageData?.story || DEFAULT_STORY;
    },
    title() {
      return this.currentBuilderPageData?.title || this.story.title || "Untitled Story";
    },
    hashtags() {
      return this.currentBuilderPageData?.hashtags || [];
    },
    storyId() {
      return this.story.story_id;
    },
    segments() {
      return this.segmentsForStory(this.currentStoryId);
    },
    segmentsForStory: state => storyId => {
      const story = state.stories[storyId]?.builderPageData?.story;
      if (!story) {
        return [];
      }
      const segmentArray = Object.values(story.segments || {});
      return (story.segment_order || []).reduce((acc, curr) => {
        const segment = segmentArray.find(seg => seg.story_segment_id === curr.story_segment_id);
        return [
          ...acc,
          segment,
        ];
      }, []);
    },
    isStoryExclusive() {
      return Boolean(this.story.is_exclusive ?? this.story.exclusive);
    },
    isStoryAllowRemix() {
      return Boolean(this.story.allow_remix);
    },
    isStoryRequiresSyndicationApproval() {
      return Boolean(this.story.requires_syndication_approval);
    },
  },
  actions: {
    setActiveStoryIdOverride(storyId) {
      this.activeStoryIdOverride = Number(storyId) || 0;
    },
    getStory(storyId) {
      if (!this.stories[storyId]) {
        throw new StoryError("Story not found", { storyId });
      }
      return this.stories[storyId];
    },
    async getOrFetchStory(storyId) {
      if (this.stories[storyId]?.storyId) {
        return this.stories[storyId];
      }
      return await this.fetchStory(storyId);
    },
    async fetchStory(storyId) {
      await this.apiGet(
        "/story",
        { story_id: storyId },
        data => {
          this.stories[storyId] = {
            ...data,
            builderPageData: this.stories[storyId]?.builderPageData,
          };
        },
        () => {},
      );
      return this.getStory(storyId);
    },
    async getStoryDataFromBuilderPage(storyId) {
      if (!storyId) {
        return null;
      }
      const publicationId = useFanPublicationStore().publicationId;

      await this.apiGet(
        "/story/get-story-data-for-page",
        {
          story_id: storyId,
          publication_id: publicationId,
        },
        data => {
          this.stories[storyId] = {
            ...this.stories[storyId],
            builderPageData: {
              story: data.story,
              title: data.title,
              hashtags: data.hashtags,
              socialNetworks: data.social_networks,
              publications: data.publications,
              variants: data.variants,
            },
          };
        },
        () => {},
      );

      return this.stories[storyId];
    },
    async loadStoryForBuilderPage() {
      if (!this.currentStoryId) {
        return null;
      }
      this.isLoadingBuilderPageData = true;
      try {
        const builderPageData = await this.getBuilderPageDataForStory(this.currentStoryId);
        if (!builderPageData) {
          return null;
        }
        this.setSocialNetworksStoreForBuilderPage(builderPageData);
        this.setCreatorPublicationStoreForBuilderPage(builderPageData);

        return builderPageData;
      } finally {
        this.isLoadingBuilderPageData = false;
      }
    },
    setTitle(title) {
      this.stories[this.currentStoryId].builderPageData.title = title;
      clearTimeout(this.debounceTitle);
      this.savingStatus = "Unsaved changes";
      this.debounceTitle = setTimeout(() => {
        this.updateTitle();
      }, 1000);
    },
    setHashtags(hashtags) {
      this.stories[this.currentStoryId].builderPageData.hashtags = hashtags;
    },
    async uploadThumbnail(storyId, file) {
      this.uploadThumbnailLoading = true;

      const formData = new FormData();
      formData.append("org_id", this.orgId);
      formData.append("image", file);

      return await $axios
          .post(`/story/${storyId}/thumbnail`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then(response => {
            this.story.thumbnail = response.data.thumbnail;
            return response;
          })
          .finally(() => {
            this.uploadThumbnailLoading = false;
          });
    },
    removeThumbnail(storyId) {
      this.uploadThumbnailLoading = true;
      this.apiPost(`/story/${storyId}/thumbnail/remove`, {}, response => {
        this.story.thumbnail = response.thumbnail;
      }, () => {}, () => {
        this.uploadThumbnailLoading = false;
      });
    },
    async uploadImageFunction(file) {
      const formData = new FormData();
      formData.append("org_id", this.orgId);
      formData.append("image", file);

      return await $axios.post("/story/image/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }).then(response => {
        return response.data.imageUrl;
      });
    },
    async uploadVideoFunction(file) {
      const formData = new FormData();
      formData.append("org_id", this.orgId);
      formData.append("video", file);

      return await $axios.post("/story/video/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }).then(response => {
        return {
          videoUrl: response.data.videoUrl,
          posterUrl: response.data.posterUrl,
        };
      });
    },
    sortHashtags(responseHashtags) {
      let sortedHashtags = [];

      this.hashtags.forEach(tag => {
        const foundTag = responseHashtags.find(responseTag => responseTag.hashtag === tag.hashtag);
        if (foundTag) {
          sortedHashtags.push(foundTag);
        }
      });

      responseHashtags.forEach(tag => {
        if (!sortedHashtags.find(sortedTag => sortedTag.hashtag === tag.hashtag)) {
          sortedHashtags.push(tag);
        }
      });

      return sortedHashtags;
    },
    addHashtags(hashtags, callback = () => {}) {
      this.hashtags.push(hashtags);
      this.loadingHashtags = true;
      this.savingStatus = "Saving...";
      this.apiPost(`/story/${this.storyId}/tag`, { hashtags }, response => {
        callback(true);
        this.setHashtags(this.sortHashtags(response.hashtags));
        this.loadingHashtags = false;
        this.savingStatus = "All Changes Saved";
      }, () => {
        callback(false);
        this.savingStatus = "Error Saving Changes";
        this.loadingHashtags = false;
      });
    },
    removeHashtag({ el, index }, callback = () => {}) {
      this.hashtags.splice(index, 1);
      const removedTagId = el.hashtag_id;
      this.loadingHashtags = true;
      this.savingStatus = "Saving...";
      this.apiPost(`/story/${this.storyId}/untag`, { hashtag_id: removedTagId }, response => {
        callback(true);
        this.savingStatus = "All Changes Saved";
        this.setHashtags(this.sortHashtags(response.hashtags));
        this.loadingHashtags = false;
      }, () => {
        callback(false);
        this.savingStatus = "Error Saving Changes";
        this.loadingHashtags = false;
      });
    },
    updateTitle(callback = () => {}) {
      this.loadingTitle = true;
      this.savingStatus = "Saving...";
      this.apiPost(`/story/${this.storyId}/title`, {
        story_title: this.title,
      }, data => {
        this.stories[this.currentStoryId].builderPageData.title = data.title;
        this.story.title = data.title;
        this.story.timestamp_updated = data.timestamp_updated;
        this.story.status = data.status;
        this.loadingTitle = false;
        this.savingStatus = "All Changes Saved";
        callback(true);
      }, () => {
        this.loadingTitle = false;
        this.savingStatus = "Error Saving Changes";
        callback(false);
      });
    },
    updateStorySeo(storyId, { seoTitle, seoDescription, seoKeywords }, successHandler = () => {}, errorHandler = () => {}) {
      this.updateStorySeoIsLoading = true;
      this.savingStatus = "Saving...";
      this.apiPost(`/story/${storyId}/seo`, {
        seo_title: seoTitle,
        seo_description: seoDescription,
        seo_keywords: seoKeywords,
      }, data => {
        this.story.seo_title = data.seo_title;
        this.story.seo_description = data.seo_description;
        this.story.seo_keywords = data.seo_keywords;
        this.story.timestamp_updated = data.timestamp_updated;
        this.savingStatus = "All Changes Saved";
        successHandler(true);
      }, () => {
        this.savingStatus = "Error Saving Changes";
        errorHandler(false);
      }, () => {
        this.updateStorySeoIsLoading = false;
      });
    },
    create(callback = () => {}, redirect = true) {
      const generalStore = useGeneralStore();

      if (!generalStore.isLoggedIn) {
        generalStore.setRedirectLocation(window.location.href);
        generalStore.redirectTo("/login");
        return;
      }

      this.loadingCreate = true;
      this.apiPost("/story/create",
        {
          org_id: this.orgId,
          user_id: this.userId,
          publishing_platform_id: useConfigurationStore().publicationId,
          publishing_platform_type: PUBLISHING_PLATFORM_TYPE.PUBLICATION,
          variant_type: STORY_VARIANT_TYPE_STORY,
        },
        response => {
          callback(response);
          if (redirect) {
            window.location.href = `${useFanPublicationStore().fanUrl}/edit-article/${response.story_id}`;
          }
          this.loadingCreate = false;
        },
        () => {
          callback(false);
          this.loadingCreate = false;
        });
    },
    update(storyId, segments, callback = () => {}) {
      const builderPageData = this.stories[storyId]?.builderPageData;

      if (!builderPageData?.story) {
        return;
      }

      clearTimeout(this.debounceUpdate);
      this.savingStatus = "Unsaved Changes";
      this.debounceUpdate = setTimeout(() => {
        this.savingStatus = "Saving...";
        this.apiPost(`/story/${storyId}`, {
          "story_version_id": builderPageData.story.current_story_version_id,
          "live_segment_order": segments.liveSegmentOrder,
          "segments": segments.segments,
        }, response => {
          callback(true);
          this.stories[storyId].builderPageData.story = {
            ...this.stories[storyId].builderPageData.story,
            ...response,
          };
          this.savingStatus = "All Changes Saved";
          this.loadingUpdate = false;
        }, () => {
          callback(true);
          this.savingStatus = "Error Saving Changes";
          this.loadingUpdate = false;
        });
      }, 1000);
    },
    publishArticle(callback = () => {}, redirect = true, storyId = null) {
      const id = storyId || this.storyId;
      this.loadingPublish = true;

      // Social-network variants publish in a background worker; poll the job for the result instead
      // of treating the immediate response as published.
      if (this.story.publishing_platform_type === PUBLISHING_PLATFORM_TYPE.SOCIAL_NETWORK) {
        this.publishToSocialNetwork(id, callback, redirect);
        return;
      }

      this.apiPost(`/story/${id}/publish`, {}, () => {
        callback(true);
        this.loadingPublish = false;
        this.story.status = STORY_PUBLISH_STATUS_ACTIVE;
        const feedStore = useMyPublicationPostFeedStore();
        const filters = feedStore.filters;
        feedStore.reload({ filters });
        if (redirect) {
          window.location.href = `${useFanPublicationStore().fanUrl}/story/${id}`;
        }
      }, () => {
        callback(false);
        this.loadingPublish = false;
      });
    },
    publishToSocialNetwork(storyId, callback = () => {}, redirect = true) {
      this.stopPublishStatusPolling();
      this.publishError = "";
      this.apiPost(`/story/${storyId}/publish`, {}, data => {
        const jobId = Number(data.social_network_publish_job_id || 0);
        if (!jobId) {
          this.failPublish(callback, "Failed to start publishing.");
          return;
        }
        this.publishJobId = jobId;
        this.pollPublishStatus(storyId, jobId, callback, redirect);
      }, () => {
        this.failPublish(callback, "Failed to start publishing.");
      });
    },
    pollPublishStatus(storyId, jobId, callback = () => {}, redirect = true) {
      this.apiGet(`/story/social-network-publish-job/${jobId}/status`, {}, data => {
        const status = data.status || "";

        if (status === "complete") {
          this.stopPublishStatusPolling();
          callback(true);
          this.loadingPublish = false;
          this.story.status = STORY_PUBLISH_STATUS_ACTIVE;
          const feedStore = useMyPublicationPostFeedStore();
          feedStore.reload({ filters: feedStore.filters });
          if (redirect) {
            window.location.href = `${useFanPublicationStore().fanUrl}/story/${storyId}`;
          }
          return;
        }

        if (status === "failed") {
          this.failPublish(callback, data.errorMessage || "Publishing failed.");
          return;
        }

        this.publishStatusPollTimeoutId = window.setTimeout(() => {
          this.pollPublishStatus(storyId, jobId, callback, redirect);
        }, PUBLISH_STATUS_POLL_INTERVAL_MS);
      }, () => {
        this.failPublish(callback, "Failed to check publishing status.");
      });
    },
    failPublish(callback, errorMessage) {
      this.stopPublishStatusPolling();
      callback(false);
      this.loadingPublish = false;
      this.publishError = errorMessage;
      this.story.status = STORY_PUBLISH_STATUS_PUBLISH_FAILED;
    },
    stopPublishStatusPolling() {
      if (this.publishStatusPollTimeoutId) {
        clearTimeout(this.publishStatusPollTimeoutId);
        this.publishStatusPollTimeoutId = null;
      }
    },
    submitArticleForReview(callback = () => {}, redirect = true, storyId = null) {
      const id = storyId || this.storyId;
      this.loadingPublish = true;
      this.apiPost(`/story/${id}/submit/review`, {}, () => {
        callback(true);
        this.loadingPublish = false;
        this.story.status = STORY_PUBLISH_STATUS_SUBMITTED_FOR_REVIEW;
        const feedStore = useMyPublicationPostFeedStore();
        const filters = feedStore.filters;
        feedStore.reload({ filters });
        if (redirect) {
          window.location.href = `${useFanPublicationStore().fanUrl}/story/${id}/edit`;
        }
      }, () => {
        callback(false);
        this.loadingPublish = false;
      });
    },
    scheduleArticle(dateTime, callback = () => {}, redirect = true, storyId = null) {
      const id = storyId || this.storyId;
      this.loadingPublish = true;
      this.apiPost(`/story/${id}/${dateTime}/schedule/publish`, {}, () => {
        callback(true);
        this.loadingPublish = false;
        this.story.status = STORY_PUBLISH_STATUS_SCHEDULED;
        const feedStore = useMyPublicationPostFeedStore();
        const filters = feedStore.filters;
        feedStore.reload({ filters });
        if (redirect) {
          window.location.href = `${useFanPublicationStore().fanUrl}/story/${id}/edit`;
        }
      }, () => {
        callback(false);
        this.loadingPublish = false;
      });
    },
    scheduleArticleNeedsReview(dateTime, callback = () => {}, redirect = true, storyId = null) {
      const id = storyId || this.storyId;
      this.loadingPublish = true;
      this.apiPost(`/story/${id}/${dateTime}/review/schedule`, {}, () => {
        callback(true);
        this.loadingPublish = false;
        this.story.status = STORY_PUBLISH_STATUS_SCHEDULED_NEEDS_REVIEW;
        const feedStore = useMyPublicationPostFeedStore();
        const filters = feedStore.filters;
        feedStore.reload({ filters });
        if (redirect) {
          window.location.href = `${useFanPublicationStore().fanUrl}/story/${this.id}/edit`;
        }
      }, () => {
        callback(false);
        this.loadingPublish = false;
      });
    },
    unpublishArticle(storyId, callback = () => {}, redirect = true) {
      this.loadingPublish = true;
      this.apiPost(`/story/${storyId}/unpublish`, {}, () => {
        callback(true);
        this.loadingPublish = false;
        this.story.status = STORY_PUBLISH_STATUS_DRAFT;
        this.savingStatus = "All Changes Saved";
        const feedStore = useMyPublicationPostFeedStore();
        const filters = feedStore.filters;
        feedStore.reload({ filters });
        if (redirect) {
          window.location.href = `${useFanPublicationStore().fanUrl}/edit-article/${storyId}`;
        }
      }, () => {
        callback(false);
        this.loadingPublish = false;
      });
    },
    rejectArticleAfterReview(callback = () => {}, redirect = true) {
      this.loadingPublish = true;
      this.apiPost(`/story/${this.storyId}/review/reject`, {}, () => {
        callback(true);
        this.loadingPublish = false;
        this.story.status = STORY_PUBLISH_STATUS_NEEDS_EDITING;
        const feedStore = useMyPublicationPostFeedStore();
        const filters = feedStore.filters;
        feedStore.reload({ filters });
        if (redirect) {
          window.location.href = `${useFanPublicationStore().fanUrl}/story/${this.storyId}/edit`;
        }
      }, () => {
        callback(false);
        this.loadingPublish = false;
      });
    },
    updateStoryExclusiveContentFlag(successHandler = () => {}, errorHandler = () => {}) {
      this.updateStoryExclusivityIsLoading = true;
      this.apiPost(`/story/${this.storyId}/exclusive`, {
        exclusive: !this.isStoryExclusive ? 1 : 0,
      }, () => {
        const isStoryExclusive = !this.isStoryExclusive;
        this.story.is_exclusive = isStoryExclusive;
        this.story.exclusive = isStoryExclusive;
        this.story.timestamp_updated = dayjs().unix();
        this.updateStoryExclusivityIsLoading = false;
        successHandler(true);
      }, () => {
        errorHandler(false);
      }, () => {
        this.updateStoryExclusivityIsLoading = false;
      });
    },
    updateStoryAllowRemixContentFlag(successHandler = () => {}, errorHandler = () => {}) {
      this.updateStoryAllowRemixContentIsLoading = true;
      this.apiPost(`/story/${this.storyId}/allow-remix`, {
        allow_remix: !this.isStoryAllowRemix ? 1 : 0,
      }, data => {
        this.story.allow_remix = !this.isStoryAllowRemix;
        this.story.timestamp_updated = data.timestamp_updated;
        successHandler(true);
      }, () => {
        errorHandler(false);
      }, () => {
        this.updateStoryAllowRemixContentIsLoading = false;
      });
    },
    updateStoryRequiresSyndicationApprovalContentFlag(successHandler = () => {}, errorHandler = () => {}) {
      this.updateStoryRequiresSyndicationApprovalContentIsLoading = true;
      this.apiPost(`/story/${this.storyId}/requires-syndication-approval`, {
        requires_syndication_approval: !this.isStoryRequiresSyndicationApproval ? 1 : 0,
      }, data => {
        this.story.requires_syndication_approval = !this.isStoryRequiresSyndicationApproval;
        this.story.timestamp_updated = data.timestamp_updated;
        successHandler(true);
      }, () => {
        errorHandler(false);
      }, () => {
        this.updateStoryRequiresSyndicationApprovalContentIsLoading = false;
      });
    },
    generateTextStoryContent(prompt, successCallback) {
      return this.apiPost("/story/generate/text", { prompt }, successCallback);
    },
    generateAiProductCollection(prompt, successCallback, errorCallback = () => {}) {
      return this.apiPost("/story/generate/products", { prompt }, successCallback, errorCallback);
    },
    generateTextStoryContentChanges(prompt, content, successCallback) {
      return this.apiPost("/story/generate/text/edit", { prompt, content }, successCallback);
    },
    async uploadStoryAudio(selectedFile) {
      this.uploadStoryAudioLoading = true;

      const formData = new FormData();
      formData.append("org_id", this.orgId);
      formData.append("audio", selectedFile);

      return await $axios
          .post("/story/audio/upload", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then(response => {
            return response.data.audioUrl;
          })
          .finally(() => {
            this.uploadStoryAudioLoading = false;
          });
    },
    validateFetchInstructionsParams(storyId, assigneeId) {
      const errors = [];
      if (!storyId) {
        errors.push("storyId is required");
      }

      if (!assigneeId) {
        errors.push("assigneeId is required");
      }

      if (errors.length) {
        this.fetchingInstructionsError = errors.join(", ");
        return false;
      }

      this.fetchingInstructionsError = "";
      return true;
    },
    validateSendInstructionsParams(storyId, senderId, assigneeId, instructions) {
      const errors = [];
      if (!storyId) {
        errors.push("storyId is required");
      }

      if (!senderId) {
        errors.push("senderId is required");
      }

      if (!assigneeId) {
        errors.push("assigneeId is required");
      }

      if (!instructions || instructions.trim() === "") {
        errors.push("instructions cannot be empty");
      }

      if (errors.length) {
        this.sendingInstructionsError = errors.join(", ");
        return false;
      }

      this.sendingInstructionsError = "";
      return true;
    },
    async getBuilderPageDataForStory(storyId) {
      if (!storyId) {
        return null;
      }
      if (!this.stories[storyId]?.builderPageData) {
        await this.getStoryDataFromBuilderPage(storyId);
      }

      return this.stories[storyId]?.builderPageData ?? null;
    },
    setSocialNetworksStoreForBuilderPage(builderPageData) {
      useSocialNetworksStore().setSocialNetworks(builderPageData.socialNetworks || []);
    },
    setCreatorPublicationStoreForBuilderPage(builderPageData) {
      useCreatorPublicationStore().setMyPublications(builderPageData.publications || []);
    },
    createStoryVariant(storyId, variant, platformId, successHandler = () => {
    }, errorHandler = () => {
    }) {
      this.isCreatingStoryVariant = true;

      this.apiPost(`/story/${storyId}/variant`,
        {
          variant,
          platform_id: platformId,
        },
        data => {
          this.isCreatingStoryVariant = false;
          successHandler(data);
        },
        () => {
          this.isCreatingStoryVariant = false;
          errorHandler();
        });
    },
  },
});
